import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Persist session_token in localStorage so auth still works when the browser
// blocks 3rd-party cookies (Safari ITP, Chrome cookie partitioning, private mode).
const TOKEN_KEY = "admissio_session_token";
export const getToken = () => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } };
export const setToken = (t) => { try { t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY); } catch {} };

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 30000, // 30s — accommodates preview-pod cold starts without the browser giving up early.
});

api.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${t}` };
  return cfg;
});

// Verbose error logging so the actual root cause is visible instead of only "Network Error".
api.interceptors.response.use(
  (res) => res,
  (err) => {
    try {
      const info = {
        url: `${err.config?.baseURL || ""}${err.config?.url || ""}`,
        method: (err.config?.method || "GET").toUpperCase(),
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        code: err.code,
        message: err.message,
      };
      // eslint-disable-next-line no-console
      console.error("[api error]", info);
    } catch {}
    return Promise.reject(err);
  }
);

// Lightweight health ping with a single retry — called on app mount to warm cold preview pods.
export const pingHealth = async () => {
  const url = `${API}/health`;
  try { await axios.get(url, { timeout: 15000 }); return true; }
  catch { try { await new Promise(r=>setTimeout(r, 1500)); await axios.get(url, { timeout: 15000 }); return true; } catch { return false; } }
};

// Wrapper that retries a single Network Error (axios 'ECONNABORTED' or no response) once after 1.5s
// — enough to survive a preview-pod cold start without surfacing "Network Error" to the user.
export const withNetworkRetry = async (fn) => {
  try { return await fn(); }
  catch (e) {
    const isNetwork = !e.response && (e.message === "Network Error" || e.code === "ERR_NETWORK" || e.code === "ECONNABORTED");
    if (!isNetwork) throw e;
    await new Promise(r => setTimeout(r, 1500));
    return await fn();
  }
};

export const CAD = (n) => {
  const v = Number(n || 0);
  return "CA$" + v.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const fmtDate = (d) => {
  if (!d) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(d))) return d;
  try { return new Date(d).toLocaleDateString("en-CA", { timeZone: "America/Toronto" }); } catch { return d; }
};

export const STAGES = [
  "New Application","Documents Pending","Application on Hold","Ready to Apply","Application In Progress",
  "Application Sent to Partner","Applied","Under College Review","Additional Documents Required",
  "PAL Applied","Conditional Offer Issued","Unconditional Offer Issued","Offer Sent to Partner",
  "Program Waitlisted","Application Rejected","Fees Pending","Fees Paid",
  "PAL Pending","PAL Issued","Admission Complete","Deferred","Withdrawn","Closed","Backed Out","Selected Another College",
];
export const REASON_STAGES = ["Backed Out", "Withdrawn", "Selected Another College"];
export const ACCEPT_STAGES = ["Fees Paid", "PAL Pending", "PAL Issued", "Admission Complete"];

export const DOC_STATUSES = ["Not Received","Partial","Received","Additional Documents Required","Complete"];
export const PRIORITIES = ["Low","Medium","High","Urgent"];
export const DECISIONS = ["Applied","Approved","Rejected","Deferred","Withdrawn"];
export const ENROLL_STATUSES = ["Enrollment Pending","Semester 1 Enrolled","Semester 1 Not Enrolled","Semester 2 Pending","Semester 2 Enrolled","Deferred","Withdrawn","Program Completed"];
export const COMMISSION_STATUSES = ["Not Eligible","Eligible","Ready to Invoice","Invoice Submitted","Payment Pending","Commission Received","Partner Payment Due","Partner Paid","Closed"];
export const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "manager", label: "Manager" },
  { value: "bdm", label: "BDM (B2B)" },
  { value: "admissions_officer", label: "Admission Officer" },
  { value: "filing_officer", label: "Filing Officer" },
  { value: "enrollment_officer", label: "Enrollment Officer (B2B)" },
  { value: "finance", label: "Finance" },
  { value: "management", label: "Management" },
];

export const stageClass = (stage) => {
  const map = {
    "New Application": "bg-slate-100 text-slate-800 border-slate-300",
    "Documents Pending": "bg-amber-50 text-amber-900 border-amber-300",
    "Application on Hold": "bg-amber-100 text-amber-900 border-amber-400",
    "Additional Documents Required": "bg-amber-50 text-amber-900 border-amber-300",
    "Ready to Apply": "bg-sky-50 text-sky-900 border-sky-300",
    "Application In Progress": "bg-sky-50 text-sky-900 border-sky-300",
    "Application Sent to Partner": "bg-cyan-50 text-cyan-900 border-cyan-300",
    "Applied": "bg-indigo-50 text-indigo-900 border-indigo-300",
    "Under College Review": "bg-blue-50 text-blue-900 border-blue-300",
    "PAL Applied": "bg-fuchsia-50 text-fuchsia-900 border-fuchsia-300",
    "Conditional Offer Issued": "bg-orange-50 text-orange-900 border-orange-300",
    "Unconditional Offer Issued": "bg-emerald-50 text-emerald-900 border-emerald-300",
    "Offer Sent to Partner": "bg-emerald-50 text-emerald-900 border-emerald-300",
    "Program Waitlisted": "bg-yellow-100 text-yellow-900 border-yellow-400",
    "Application Rejected": "bg-red-100 text-red-900 border-red-400 font-semibold",
    "Fees Pending": "bg-yellow-50 text-yellow-900 border-yellow-300",
    "Fees Paid": "bg-teal-50 text-teal-900 border-teal-300",
    "PAL Pending": "bg-rose-50 text-rose-900 border-rose-300",
    "PAL Issued": "bg-rose-50 text-rose-900 border-rose-300",
    "Admission Complete": "bg-green-100 text-green-900 border-green-400 font-semibold",
    "Deferred": "bg-gray-100 text-gray-700 border-gray-300",
    "Withdrawn": "bg-red-50 text-red-800 border-red-300",
    "Closed": "bg-slate-100 text-slate-600 border-slate-300",
    "Backed Out": "bg-zinc-200 text-zinc-700 border-zinc-400 line-through",
    "Selected Another College": "bg-violet-50 text-violet-800 border-violet-300",
  };
  return map[stage] || "bg-slate-100 text-slate-700 border-slate-300";
};

export const priorityClass = (p) => ({
  Urgent: "bg-red-50 text-red-800 border-red-300",
  High: "bg-orange-50 text-orange-800 border-orange-300",
  Medium: "bg-blue-50 text-blue-800 border-blue-300",
  Low: "bg-slate-50 text-slate-700 border-slate-300",
}[p] || "bg-slate-50 text-slate-700 border-slate-300");
